import React, { useState, useEffect } from 'react';
import { IoClose, IoTrash, IoPencil, IoWarningOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { db } from '../../firebaseConfig';
// eslint-disable-next-line no-unused-vars
import { collection, query, where, getDocs, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import EditarGastoModal from './EditarGastoModal';

const DetalleCxPModal = ({ provider, month, year, onClose, onSuccess }) => {
    const { userData } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingGasto, setEditingGasto] = useState(null);

    // Modal Confirmation & Notification States
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [successNotification, setSuccessNotification] = useState(null);
    const [errorNotification, setErrorNotification] = useState(null);

    useEffect(() => {
        if (userData?.condominioId && provider) {
            fetchInvoices();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, provider]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const reportDate = moment().year(year).month(month).endOf('month');

            const q = query(
                collection(db, "gastos"),
                where("condominioId", "==", userData.condominioId),
                where("proveedor", "==", provider),
                where("tipo", "==", "egreso")
            );

            const snap = await getDocs(q);
            const list = [];

            snap.forEach(doc => {
                const gasto = doc.data();
                const createdAt = gasto.fecha?.toDate ? moment(gasto.fecha.toDate()) : moment();

                // Same logic as ReporteCxP to filter
                if (createdAt.isAfter(reportDate, 'day')) return;

                let isOpen = false;
                if (gasto.provisionado || gasto.estatus === 'pendiente') {
                    isOpen = true;
                    if (gasto.estatus === 'pagado') {
                        const paidAt = gasto.fechaPago?.toDate ? moment(gasto.fechaPago.toDate()) : null;
                        if (paidAt && paidAt.isAfter(reportDate)) {
                            isOpen = true;
                        } else {
                            isOpen = false;
                        }
                    }
                }

                if (isOpen) {
                    list.push({ id: doc.id, ...gasto });
                }
            });

            // Sort by Date Desc
            list.sort((a, b) => b.fecha?.seconds - a.fecha?.seconds);
            setInvoices(list);

        } catch (error) {
            console.error("Error fetching invoices:", error);
        } finally {
            setLoading(false);
        }
    };

    const executeDelete = async () => {
        if (!deleteTarget) return;
        const gasto = deleteTarget;
        setDeleteTarget(null);

        try {
            const batch = writeBatch(db);

            // 1. Delete Gasto
            const gastoRef = doc(db, 'gastos', gasto.id);
            batch.delete(gastoRef);

            // 2. Find and Delete Ledger Entry (Asiento)
            const qAsiento = query(
                collection(db, 'contabilidad_asientos'),
                where('referenciaId', '==', gasto.id)
            );
            const snapAsiento = await getDocs(qAsiento);
            snapAsiento.forEach(doc => {
                batch.delete(doc.ref);
            });

            await batch.commit();

            setSuccessNotification("Factura eliminada correctamente.");
            fetchInvoices(); // Refresh list
            if (onSuccess) onSuccess(); // Notify parent to refresh report

        } catch (error) {
            console.error("Error deleting invoice:", error);
            setErrorNotification("Error al eliminar la factura.");
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 999 }}>
            {deleteTarget && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#f59e0b" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            ¿Eliminar Factura?
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            ¿Seguro que quieres eliminar esta factura de <strong>{deleteTarget.proveedor}</strong> ({deleteTarget.descripcion})? Esta acción no se puede deshacer.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={() => setDeleteTarget(null)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={executeDelete}
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {successNotification && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoCheckmarkCircleOutline size={60} color="#10b981" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Factura Eliminada
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {successNotification}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setSuccessNotification(null)}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {errorNotification && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#ef4444" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Error
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {errorNotification}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setErrorNotification(null)}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}

            <div className="modal-content" style={{ maxWidth: '700px' }}>
                <div className="modal-header">
                    <h3>Detalle de Cuentas por Pagar: {provider}</h3>
                    <button onClick={onClose} className="close-btn"><IoClose /></button>
                </div>

                <div className="modal-body">
                    {loading ? <p>Cargando facturas...</p> : (
                        <div className="table-responsive">
                            <table className="styled-table" style={{ fontSize: '0.9em' }}>
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Vencimiento</th>
                                        <th>Descripción</th>
                                        <th style={{ textAlign: 'right' }}>Original</th>
                                        <th style={{ textAlign: 'right' }}>Pagado</th>
                                        <th style={{ textAlign: 'right' }}>Saldo</th>
                                        <th style={{ textAlign: 'center' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.length === 0 ? (
                                        <tr><td colSpan="7" style={{ textAlign: 'center' }}>No se encontraron facturas pendientes.</td></tr>
                                    ) : invoices.map(inv => {
                                        const paid = inv.montoPagado || 0;
                                        const remaining = inv.monto - paid;
                                        return (
                                            <tr key={inv.id}>
                                                <td>{moment(inv.fecha?.toDate()).format('DD/MM/YYYY')}</td>
                                                <td>{inv.fechaVencimiento ? moment(inv.fechaVencimiento.toDate()).format('DD/MM/YYYY') : '-'}</td>
                                                <td>{inv.descripcion}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div>${new Intl.NumberFormat('es-MX').format(inv.monto)}</div>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ color: '#16a34a' }}>${new Intl.NumberFormat('es-MX').format(paid)}</div>
                                                </td>
                                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>
                                                    ${new Intl.NumberFormat('es-MX').format(remaining)}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        className="icon-btn"
                                                        onClick={() => setEditingGasto(inv)}
                                                        title="Editar Factura"
                                                        style={{ color: '#2563eb', marginRight: '8px' }}
                                                    >
                                                        <IoPencil />
                                                    </button>
                                                    <button
                                                        className="icon-btn danger"
                                                        onClick={() => setDeleteTarget(inv)}
                                                        title="Eliminar Factura/Provisión"
                                                        style={{ color: '#dc2626' }}
                                                    >
                                                        <IoTrash />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {editingGasto && (
                    <EditarGastoModal
                        gasto={editingGasto}
                        onClose={() => setEditingGasto(null)}
                        onSuccess={() => {
                            fetchInvoices();
                            if (onSuccess) onSuccess(); // updates parent totals
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default DetalleCxPModal;
